import { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

import { ApplyPage } from 'src/components/ApplyPage/ApplyPage';
import { External } from 'src/components/External/External';
import Layout from 'src/components/Layout/Layout';
import { removeSessionStorage } from 'src/components/References/ReferencesList';
import { useConfig } from 'src/context/config';
import { useApplicationCorrelationId } from 'src/context/use-application-correlation-id';
import { useAuth } from 'src/context/useAuth';
import { useJobId } from 'src/context/useJobId';
import { useGetSignInUrl } from 'src/hooks/useGetSignInUrl';
import { FullScreenLoader } from 'src/ui/FullScreenLoader/FullScreenLoader';
import { SomethingWentWrong } from 'src/ui/SomethingWentWrong/SomethingWentWrong';
import { navigateToUrl } from 'src/utils';

import { useInitApp } from './useInitApp';

const App = () => {
  useEffect(() => {
    removeSessionStorage();
  }, []);

  const { jobId } = useJobId();
  const { config } = useConfig();
  const { isAuth, loading: authLoading } = useAuth();
  const signInUrl = useGetSignInUrl();
  const { applicationCorrelationId, newApplication: isNewApplication } =
    useApplicationCorrelationId();
  const location = useLocation();

  const { loading, error, isLinkOut } = useInitApp({
    jobId,
    config,
    applicationCorrelationId,
    isNewApplication,
  });

  if (loading || authLoading) {
    return <FullScreenLoader />;
  }

  if (error) {
    return (
      <Layout>
        <SomethingWentWrong
          error={error}
          type="job-init-error"
          message="An error occurred when initialising job application"
          applicationCorrelationId={applicationCorrelationId}
          jobId={jobId}
        />
      </Layout>
    );
  }

  if (!isAuth) {
    navigateToUrl(signInUrl, true);

    return null;
  }

  const isExternalUrl = location.pathname.endsWith('/external');
  const isLinkOutSuccess = isLinkOut && isExternalUrl;

  if (isLinkOut && !isLinkOutSuccess) {
    return <External />;
  }

  return (
    <Routes>
      <Route path="/:lang?/job/:id/apply/*" element={<Layout />}>
        <Route
          path="*"
          element={<ApplyPage isLinkOutSuccess={isLinkOutSuccess} />}
        />
      </Route>
    </Routes>
  );
};

export default App;
